<template>
  <div>
    <div>
      <md-radio
        v-for="level in itemCategoryLevels"
        :value="level.level"
        :model="itemCategoryLevel"
        @change="$emit('update:itemCategoryLevel', $event)"
        :key="level.level"
        >{{ level.name }}</md-radio
      >
    </div>
    <div>
      <!-- 新商品絞り込みチェック -->
      <div class="content" v-if="enableFilterNewItems">
        <md-checkbox v-model="_filterNewItems">
          新商品に絞る
        </md-checkbox>
      </div>
    </div>
    <gl-tree-view
      :entries="itemCategories"
      :selected="categories"
      @update:selected="$emit('update:categories', $event)"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import GlTreeView from '@/components/common/GlTreeView.vue'
import ErrorReport from '@/mixins/ErrorReport'
import { collectCodes } from '@/util/list'

/**
 * 商品分類階層レベルを階層インデックス（何階層目か、0始まり）を取得する
 */
function toCateLevelToTreeLevel(itemCategoryLevels, targetLevel) {
  return itemCategoryLevels.findIndex(l => l.level === targetLevel)
}

/**
 * ノードの再帰処理
 *
 * 上から処理対象とする。
 */
function walk(entry, level, f) {
  f(entry, level)
  if (entry.children) {
    for (const child of entry.children) {
      walk(child, level + 1, f)
    }
  }
}

/**
 * 「商品モード - 分類選択時」コンポーネント
 */
export default {
  components: { GlTreeView },
  mixins: [ErrorReport],
  props: {
    /**
     * 分類をロードする対象の企業コード
     */
    company: {
      type: String,
    },
    /**
     * 選択している分類階層
     */
    itemCategoryLevel: {
      type: Number,
    },
    /**
     * 選択されているカテゴリ（表示されている最下層）
     */
    categories: {
      type: Array,
      default: () => [],
    },
    /**
     * POSの開示制限あり
     */
    posRestriction: {
      type: Boolean,
      default: false,
    },
    /**
     * 新商品のみに絞り込むかどうかのユーザー選択を有効にするか否か
     *
     * 絞り込みはあくまで分析結果であって、このフラグをOnにしてもツリー表示や
     * 商品検索には影響ありません。
     */
    enableFilterNewItems: {
      type: Boolean,
      default: false,
    },
    /**
     * 分類指定、商品選択での新商品絞り込み
     */
    filterNewItems: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * 分類階層レベルマスタ
       */
      itemCategoryLevels: [],
      /**
       * 分類の階層構造データ
       */
      itemCategoriesCache: [],
      /**
       * ツリー用の分類の階層構造データ
       *
       * 選択されている階層に応じて階層構造データが変わる（選択されている階層
       * 以下の階層は表示しない）ので、分類データ全体のキャッシュとは別に分類
       * データを保持する
       * TODO 大きくなる傾向のデータなので、なるべくメモリに持ちたくない、、、
       */
      itemCategories: [],
    }
  },
  watch: {
    /**
     * 企業が決まることで階層一覧と分類一覧が取得できる
     */
    company: {
      immediate: true,
      async handler(company, oldCompany) {
        if (!company) {
          return
        }

        let categories = this.categories
        let level = this.itemCategoryLevel
        if (oldCompany) {
          categories = []
          this.$emit('update:categories', [])
          level = undefined
        }

        this.itemCategories = []

        this.itemCategoryLevels = this.$store.state.itemCategoryLevels[company]

        this.itemCategoriesCache = await this.$repositories
          .get('itemCategory')
          .get(company, this.posRestriction)

        // カテゴリーコードを選択可能なものに絞り込む
        // 手動で変更時（oldCompanyがある場合）はスキップ
        if (!oldCompany) {
          const set = collectCodes(this.itemCategoriesCache, 'code')
          categories = this.categories.filter(category => set.has(category))
          this.$emit('update:categories', categories)
        }

        if (!level) {
          // 必ずどこかの階層選択は必要なので、指定されていない場合は最下層とする
          this.$emit(
            'update:itemCategoryLevel',
            this.itemCategoryLevels[this.itemCategoryLevels.length - 1].level,
          )
        }

        this.setEntries(categories)
      },
    },
    itemCategoryLevel(newVal, oldVal) {
      // 階層を変えた場合はもともと選択されていた要素は保持しないので、
      // 親コンポーネントに対して選択をクリアする旨を通知する必要がある。
      // ただし、コンポーネントロード直後（初回）変更時には親からもらった
      // データで描画するので何もする必要はない。
      if (oldVal !== undefined) {
        this.$emit('update:categories', [])
        this.setEntries(this.categories)
      }
    },
  },
  computed: {
    /**
     * 分類指定、商品選択での新商品絞り込み
     */
    _filterNewItems: {
      get() {
        return this.filterNewItems
      },
      set(newVal) {
        this.$emit('update:filterNewItems', newVal)
      },
    },
    /**
     * 入力エラー
     */
    errors() {
      const errors = []

      if (!this.categories || this.categories.length === 0) {
        errors.push('分類を選択してください')
      }

      return errors
    },
  },
  methods: {
    setEntries(categories) {
      const levelUntil = toCateLevelToTreeLevel(
        this.itemCategoryLevels,
        this.itemCategoryLevel,
      )

      // TODO 一回ツリー構造を全部Cloneしてから指定分類配下を削除している
      //      パフォーマンスの問題が出たらいい方法を考える。
      const filteredByLevel = cloneDeep(this.itemCategoriesCache)

      if (levelUntil < this.itemCategoryLevels.length - 1) {
        for (const top of filteredByLevel) {
          walk(top, 0, (entry, level) => {
            if (level === levelUntil) {
              entry.children = undefined
            }
          })
        }
      }

      if (categories.length > 0) {
        const categoriesSet = new Set(categories)

        for (const topLevelElement of filteredByLevel) {
          walk(topLevelElement, 0, entry => {
            if (entry.children) {
              return
            }
            entry.state = categoriesSet.has(entry.code) ? 'checked' : 'none'
          })
        }
      }

      if (filteredByLevel.length > 0) {
        this.itemCategories = [
          {
            code: undefined,
            name: '全分類',
            children: filteredByLevel,
            open: true,
          },
        ]
      } else {
        this.itemCategories = []
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.line {
  margin-top: 32px;
  &:first-child {
    margin-top: 0;
  }
}

.tree-view-container {
  height: 350px;
  overflow: auto;
  border: 1px solid var(--color-border);
  border-radius: 5px;
  padding: var(--spacing-medium);
}
</style>
